// Spectator-end overlay — replaces victory / game-over for staff
// watching a game. Shows when the spectator session has played
// through to the adventure's outcome (party wiped or completed).
// Offers Rewatch (start over from event 0) and Exit (back to the
// staff games list) — no Retry, since the spectator isn't playing.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class SpectatorEndOverlay extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    outcome: { type: String }, // 'completed' | 'wiped'
  };

  static styles = css`
    :host {
      display: block;
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.6s ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .content {
      text-align: center;
      color: #e5e5e5;
      animation: scaleIn 0.6s ease-out;
    }

    @keyframes scaleIn {
      from { transform: scale(0.85); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }

    .title {
      font-size: 2rem;
      font-weight: 700;
      margin: 0 0 0.5rem;
      color: #ffd75e;
    }

    .subtitle {
      font-size: 1rem;
      color: #a1a1aa;
      margin: 0 0 2rem;
    }

    .button-row {
      display: flex;
      gap: 0.75rem;
      justify-content: center;
    }

    button {
      padding: 0.75rem 1.75rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition: transform 0.15s, background 0.15s, box-shadow 0.15s;
    }

    .rewatch {
      background: linear-gradient(135deg, #ffd75e, #d4a017);
      color: #1a1a1a;
      border: none;
    }
    .rewatch:hover { transform: scale(1.04); box-shadow: 0 0 18px rgba(255, 215, 94, 0.45); }
    .rewatch:active { transform: scale(0.98); }

    .exit {
      background: #2a2a2a;
      color: #e5e5e5;
      border: 1px solid #555;
    }
    .exit:hover { background: #3a3a3a; }
  `;

  constructor() {
    super();
    this.show = false;
    this.outcome = 'completed';
  }

  _handleRewatch() {
    this.dispatchEvent(new CustomEvent('rewatch', { bubbles: true, composed: true }));
  }

  _handleExit() {
    this.dispatchEvent(new CustomEvent('exit-spectator', { bubbles: true, composed: true }));
  }

  render() {
    if (!this.show) return html``;
    const title = this.outcome === 'wiped' ? 'The party fell.' : 'Adventure complete.';
    const subtitle = 'You watched the whole run.';
    return html`
      <div class="overlay">
        <div class="content">
          <h1 class="title">${title}</h1>
          <p class="subtitle">${subtitle}</p>
          <div class="button-row">
            <button class="rewatch" @click="${this._handleRewatch}">Rewatch</button>
            <button class="exit" @click="${this._handleExit}">Exit</button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('spectator-end-overlay', SpectatorEndOverlay);
